<template>

<div class="main list-view no-sub-header">

	<div class="filters">
		<div class="label-group">
			<label>Start</label>
			<input type="date" v-model="filters.start_date" :max="$today().clone().format('YYYY-MM-DD')">
		</div>
		<div class="label-group">
			<label>End</label>
			<input type="date" v-model="filters.end_date" :max="$today().clone().format('YYYY-MM-DD')">
		</div>
		<div class="label-group">
			<label></label>
			<div class="button small neutral" @click="getList(1)">Filter Dates</div>
		</div>

		<div class="divider"></div>
        <ul class="checkbox-group">
            <li>
                <span :class="['pointer', 'far', filters.taxed ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('taxed')"></span>
                <label>Items with tax charged</label>
            </li>
            <li>
                <span :class="['pointer', 'far', filters.not_taxed ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('not_taxed')"></span>
                <label>Items with no tax charged</label>
            </li>
            <li>
                <span :class="['pointer', 'far', filters.by_department ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('by_department')"></span>
                <label>Group by department</label>
            </li>
        </ul>

		<div v-if="taxRates.length > 1">
			<div class="divider"></div>
			<div class="label-group">
				<label>Tax Rate</label>
				<select v-model="filters.tax_rate" @change="getList(1)">
					<option value="">All</option>
					<option v-for="rate in taxRates" :value="rate">{{ (rate * 100) + '%' }}</option>
				</select>
			</div>
		</div>

		<div class="divider"></div>
        <div class="button neutral" @click="clearFilters(1)">Clear Filters</div>
        <div v-if="orderDetail.order_id" class="button neutral" @click="getList()">Back to Report</div>
		<div v-if="!orderDetail.order_id">
			<div class="divider"></div>
			<div :class="['extra-menu-item', gettingExport ? 'disabled' : 'neutral']" @click="doExport()">Export CSV</div>
			<div class="extra-menu-item" @click="printWindow()">Print {{ paging.total_pages > 1 ? 'This Page' : 'Report' }}</div>
			<div class="message">{{ exportMessage }}</div>
		</div>
		<div v-if="legacyLink">
			<div class="divider"></div>
			<router-link :to="legacyLink" class="extra-menu-item">Legacy Version</router-link>
		</div>
		<div v-if="getDevEnvironment" class="extra-menu-item" @click="getList()">Reload Report</div>
	</div>

	<div v-if="orderDetail.order_id" class="detail">
		<div v-if="loading" class="loading empty-list"><i class="fa fa-spinner fa-spin" /></div>
		<order-detail v-else :orderDetail="orderDetail" />
	</div>

	<div v-else class="list report-list">
		<div class="printable-heading">Taxed Item Report{{ filters.tax_rate ? ' (' + (filters.tax_rate * 100) + '%)' : '' }} {{ filters.start_date }} through {{ filters.end_date }}</div>

		<div v-if="list.length > 0" class="sub-header" style="grid-template-columns: 1.33fr 1fr; align-items: center;">
			<div class="two-columns" style="font-size: 1.25em; padding: 0 0 0 0.5em;">
				<div><label>Total Taxed{{ filters.tax_rate ? ' at ' + (filters.tax_rate * 100) + '%' : '' }}</label> <b>{{ $formatCurrency(totals.taxable) }}</b></div>
				<div><label>Total Not Taxed</label> <b>{{ $formatCurrency(totals.not_taxable) }}</b></div>
			</div>
			<div v-if="!filters.by_department" class="controls">
				<pagination :paging="paging" @updatePage="goToPage"></pagination>
			</div>
		</div>

	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>

	    <table v-else class="data-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th v-if="!filters.by_department" class="list">Number</th>
					<th v-if="!filters.by_department" class="list">Date</th>
					<th v-if="!filters.by_department" class="list">UPC</th>
					<th class="list">{{ filters.by_department ? 'Department' : 'Description' }}</th>
					<th class="list right">Qty</th>
					<th v-if="!filters.by_department" class="list right">Each</th>
					<th class="list right">Taxed</th>
					<th class="list right">Not Taxed</th>
				</tr>
			</thead>
			<tr v-for="item in list" :class="{'clickable-list-item':item.order_id}" @click="getOrder(item.order_id)">
				<td v-if="!filters.by_department" class="list left">{{ item.order_number }}</td>
				<td v-if="!filters.by_department" class="list left">{{ $moment(item.order_date).format('MM/DD/YY') }}</td>
                <td v-if="!filters.by_department" class="list left">{{ item.upc }}</td>
                <td class="list left">{{ item.description }}</td>
                <td class="list right">{{ item.quantity }}</td>
                <td v-if="!filters.by_department" class="list right">{{ $formatCurrency(item.order_price) }}</td>
                <td v-if="filters.by_department" class="list right">{{ $formatCurrency(item.taxable) }}</td>
                <td v-else class="list right">{{ item.not_taxable ? '0.00' : $formatCurrency(item.order_price * item.quantity) }}</td>
                <td v-if="filters.by_department" class="list right">{{ $formatCurrency(item.not_taxable) }}</td>
                <td v-else class="list right">{{ item.not_taxable ? $formatCurrency(item.order_price * item.quantity) : '0.00' }}</td>
			</tr>
		</table>

	</div>

	<message-modal v-if="message" :message="message" :loading="loading" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

	<help-popup :helpLink="{240:'Item Taxable Report'}" :tours="false"></help-popup>

</div>

</template>

<script>
import OrderDetail from '@/components/OrderDetail.vue'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'order-items',
    components: {
        'order-detail': OrderDetail,
		'help-popup': HelpPopup
    },
	data() {
		return {
            loading: true,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			searchTip: 'Search for a product by UPC or description.',
			filters: {},
			taxRates: [],
            default_start_date: '',
            default_end_date: '',
			list: [],
			paging: {},
			totals: {},
			orderDetail: {},
			exportMessage: '',
			gettingExport: false,
			legacyLink: ''
		}
	},
	created() {
		this.clearFilters()
	},
	methods: {
		clearFilters() {
			this.filters = {
				page: 1,
				start_date: '',
				end_date: '',
				do_export: false,
				taxed: false,
				not_taxed: false,
				by_department: false,
				tax_rate: ''
			}
			this.default_start_date = new Date().toISOString().slice(0,10)
			this.default_end_date = new Date().toISOString().slice(0,10)
			this.filters.start_date = this.$route.query.start || this.default_start_date
			this.filters.end_date = this.$route.query.end || this.default_end_date
			this.getList()
		},
		getTotals() {
			this.totals = {}
			// copy filters to totalsFilters
			let totalsFilters = JSON.parse(JSON.stringify(this.filters))
			totalsFilters.totals_only = true
			this.apiCall({ destination: 'get_report_order_items', data: totalsFilters }).then(obj => {
				this.totals = obj.totals
				if (obj.tax_rates && obj.tax_rates.length > 1) {
					this.taxRates = obj.tax_rates
				}
			}).catch(err => {
				this.loading = false
				this.messageStatus = 'error'
				this.message = "There was an error loading this report."
			})
		},
		getList(page) {
			this.getTotals()
			this.orderDetail = {}
			this.loading = true
			if (page) {
				this.filters.page = page
			}
			// get number of days between start and end dates
			let start = new Date(this.filters.start_date)
			let end = new Date(this.filters.end_date)
			let days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1
			if (days > 366) {
				this.message = 'Date range greater than one year not supported.'
				this.messageStatus = 'error'
				this.loading = false
				return
			}
			this.message = 'Generating report'
			if (days > 1) {
				this.message += ' for ' + days + ' days'
			}
			this.message += '...'
			if (days > 20) {
				this.message += '<br>(this may take a few minutes)'
			}
			this.apiCall({ destination: 'get_report_order_items', data: this.filters }).then(obj => {
				this.loading = false
				this.message = obj.message
				this.messageStatus = obj.status
				if (this.filters.do_export) {
					this.filters.do_export = 0
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
						this.exportMessage = ''
					}, 500)
				} else {
					this.list = obj.list
					this.paging = obj.paging
					if (obj.legacy_link) {
						this.legacyLink = obj.legacy_link
					}
				}
			}).catch(err => {
				this.loading = false
				this.messageStatus = 'error'
				this.message = "There was an error loading this report."
			})
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
            if (which == 'taxed' && this.filters.taxed) {
                this.filters.not_taxed = false
            }
            if (which == 'not_taxed' && this.filters.not_taxed) {
                this.filters.taxed = false
            }
			this.filters.page = 1
            this.getList()
		},
		getOrder(orderId) {
			if (this.loading || !orderId) { return }
			this.loading = true
			this.orderDetail = { order_id: orderId }
			this.apiCall({ destination: 'get_item_order', data: { order_id: orderId } }).then(obj => {
				this.orderDetail.order = obj.order_detail
				this.orderDetail.billing = obj.order_billing_address
				this.orderDetail.custom_fields = obj.order_billing_custom_fields
				this.orderDetail.shipping = obj.order_shipping_address
				this.orderDetail.items = obj.order_items
				this.orderDetail.discounts = obj.order_discounts
				this.orderDetail.fees = obj.order_fees
				this.orderDetail.item_fees = obj.order_item_fees
				this.orderDetail.payments = obj.order_payments
				this.orderDetail.totals = obj.order_totals
				this.loading = false
			})
		},
		printWindow() {
			window.print()
		},
		doExport() {
			if (this.gettingExport) { return false }
			this.gettingExport = true
			this.exportMessage = 'Generating CSV for ' + this.list.length + ' items...'
			this.filters.product_id = ''
			this.filters.do_export = true
			this.getList(1)
		}
	}
}
</script>
